import { FC, Fragment } from "react";
import { FormattedMessage } from "react-intl";
import { Grid } from "@mui/material";
import { stringToHex, toBytes } from "viem";

import { SelectInput } from "@dzambalaorg/mui-inputs-core";
import { useMetamask, useServerSignature, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import { Breadcrumbs, PageHeader } from "@dzambalaorg/mui-page-layout";
import { FormWrapper } from "@dzambalaorg/mui-form";
import type { IServerSignature } from "@dzambalaorg/types-blockchain";
import { AddressType, ContractFeatures, type IContract, TokenType } from "@framework/types";
import BreedABI from "@framework/abis/json/ExchangeBreedFacet/breed.json";
import { ZeroAddress } from "@framework/constants";

import { CommonContractInput } from "../../../../../components/inputs/common-contract";
import { TemplateInput } from "./template-input";
import { TokenInput } from "./token-input";
import { validationSchema } from "./validation";
import { StyledSubtitle } from "./styled";

export interface IBreedDto {
  tokenType: TokenType;
  mom: {
    address: string;
    contractId: number;
    templateId: number;
    tokenId: number;
    token: {
      tokenId: string;
    };
  };
  dad: {
    address: string;
    contractId: number;
    templateId: number;
    tokenId: number;
    token: {
      tokenId: string;
    };
  };
}

export const Breed: FC = () => {
  const referrer = ZeroAddress;

  const { writeContractAsync } = useWriteContractWithSimulation();

  const metaFnWithSign = useServerSignature(
    async (values: IBreedDto, sign: IServerSignature, systemContract: IContract) => {
      return writeContractAsync({
        address: systemContract.address as AddressType,
        abi: BreedABI,
        functionName: "breed",
        args: [
          {
            nonce: toBytes(sign.nonce, { size: 32 }),
            externalId: toBytes(sign.bytecode || ""),
            expiresAt: sign.expiresAt,
            referrer: ZeroAddress,
            extra: stringToHex("0x", { size: 32 }),
          },
          {
            tokenType: Object.values(TokenType).indexOf(values.tokenType),
            token: values.mom.address,
            tokenId: values.mom.token.tokenId,
            amount: 1,
          },
          {
            tokenType: Object.values(TokenType).indexOf(values.tokenType),
            token: values.dad.address,
            tokenId: values.dad.token.tokenId,
            amount: 1,
          },
          sign.signature,
        ],
      });
    },
  );

  const { execute: metaFn } = useMetamask((values: IBreedDto) => {
    return metaFnWithSign(
      {
        url: "/breed/sign",
        method: "POST",
        data: {
          referrer,
          momId: values.mom.tokenId,
          dadId: values.dad.tokenId,
        },
      },
      values,
    ) as Promise<void>;
  });

  const handleSubmit = async (values: IBreedDto) => {
    await metaFn(values);
  };

  return (
    <Fragment>
      <Breadcrumbs path={["dashboard", "breed"]} />

      <PageHeader message="pages.breed.title" />

      <FormWrapper
        initialValues={{
          tokenType: TokenType.ERC721,
          mom: {
            address: ZeroAddress,
            tokenId: 0,
            token: {
              tokenId: "0",
            },
          },
          dad: {
            address: ZeroAddress,
            tokenId: 0,
            token: {
              tokenId: "0",
            },
          },
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        showPrompt={false}
        testId="BreedForm"
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <StyledSubtitle variant="h6">
              <FormattedMessage id="pages.breed.matron" />
            </StyledSubtitle>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <StyledSubtitle variant="h6">
              <FormattedMessage id="pages.breed.sire" />
            </StyledSubtitle>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <SelectInput
              name="tokenType"
              options={TokenType}
              disabledOptions={[TokenType.NATIVE, TokenType.ERC20, TokenType.ERC1155]}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <CommonContractInput
              name="mom.contractId"
              data={{ contractFeatures: [ContractFeatures.GENES] }}
              withTokenType
            />
            <TemplateInput prefix="mom" />
            <TokenInput prefix="mom" />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <CommonContractInput
              name="dad.contractId"
              data={{ contractFeatures: [ContractFeatures.GENES] }}
              withTokenType
            />
            <TemplateInput prefix="dad" />
            <TokenInput prefix="dad" />
          </Grid>
        </Grid>
      </FormWrapper>
    </Fragment>
  );
};
